
import { IDictionary } from 'util/domain';
import { CommonUtilsService } from 'util/services/commonUtilsService';
import { 
	PermissionType,
	ObjectType,
	RightsOption,
	DefaultRight,
	AclPrincipal,
	ServerAcl,
	TypeGuards,
	RightsType,
	ShareInfo,
	DocumentShare,
	FolderShare,
	LibraryShare,
	AdvancedOption,
	LegacyLinkAcl,
	ReceivedShareInfo,
	LinkAcl
} from 'phoenix/domain/phoenix-acl';
import SettingsService from 'phoenix/services/settingsService';

const PermissionsMetaData = (function() {
	var values = Object.keys(PermissionType).filter(k => +k > 0);
	return {
		All: 0xE77000, 
		// find the highest value in the bitfield enum so it doesn't need to be maintained by hand
		Max: values.reduce((prev, curr) => {
			var val = +curr;
			return  val > prev ? val : prev;
		}, 0)
	};
})();

export default class Acl {
	public sourceName = '';
	public sourceId = 0;
	public sourceObjectType = ObjectType.UNDEFINED;
	public type = ObjectType.UNDEFINED;
	public allowPerms = PermissionType.NONE;
	public denyPerms = PermissionType.NONE;
	public isInherited = false;			
	public isLinkAcl = false;
	public isOwnerAcl = false;
	public isModifiedRight = false;
	public rightId = 0;
	public id: number | string;
	public userId: number;
	public elementId: string;			
	public allowAdvancedSettings: boolean;
	public isGroup:boolean;
	public userName:string;
	public webPath:string = '';

	public get rightName() {
		var name = this.Rights[this.rightId].name;
		return name;				
	}

	
	public get source(){
		return this.srvData;
	}

	constructor(
		private util:CommonUtilsService,
		private Rights:IDictionary<DefaultRight>, 
		private RightsOptions:RightsOption[][], 
		private ObjectTypeMappings:{[id:number]:string}, 
		private settingsService:SettingsService,
		private srvData:AclPrincipal, 
		itemType:ObjectType, 
		userName:string,
		isGroup:boolean = false,
		userId:number = 0
	) {
		srvData = angular.isDefined(srvData) && srvData !== null ? srvData : {} as ServerAcl;
		if (isServerAcl(srvData)) {
			// this acl is an override of an existing acl
			this.type = itemType;
			this.userId = srvData.userId;
			this.userName = srvData.userName;
			this.isGroup = srvData.isGroup;
			this.elementId = this.generateUniqueId();
			this.isInherited = true;
			return;
		} else if (isLegacyLinkAcl(srvData)) {
			// this acl is for a link (old version)
			this.id = srvData.ReferenceString;
			this.type = (angular.isDefined(srvData.FolderName) && srvData.FolderName) ? ObjectType.FOLDER : ObjectType.DOCUMENT;
			this.userId = 0;
			this.userName = 'anonymous';
			this.isGroup = false;
			this.isLinkAcl = true;
		} else if (isLinkAcl(srvData)) {
			// this acl is for a link
			this.id = srvData.ReferenceString;
			this.type = srvData.ObjectType === 'Folder' ? ObjectType.FOLDER : ObjectType.DOCUMENT;
			this.userId = 0;
			this.userName = 'anonymous';
			this.isGroup = false;
			this.isLinkAcl = true;
			this.allowPerms = srvData.Permissions;
			this.isInherited = isInheritedShare(srvData);
		}
		else if (typeof srvData === 'number') {
			// this acl is constructed from an integer bit field
			this.allowPerms = srvData;
			this.userId = 0;
			this.userName = userName || 'anonymous';
			this.isGroup = angular.isDefined(isGroup) ? isGroup : false;
			this.type = itemType;
			this.updatePermissions();
			return;
		} 
		else if (userName) {
			// this acl is for a new acl
			this.type = itemType;
			this.userId = angular.isDefined(userId) ? userId : 0;
			this.userName = userName;
			this.isGroup = isGroup;
			this.elementId = this.generateUniqueId();
			return;
		}
		else {
			// construct an Acl from a data object returned from a server API call
			if (TypeGuards.isFolderShare(srvData)) {
				// acl is folder
				this.webPath = `${this.settingsService.serverWebPath}/folder/${srvData.FolderId}`;
				this.id = srvData.FolderACLId;
				this.type = ObjectType.FOLDER;
			} else if (TypeGuards.isLibraryShare(srvData)) {
				// acl is library
				this.webPath = `${settingsService.serverWebPath}/library/${srvData.LibraryId}`;
				this.id = srvData.LibraryACLId;
				this.type = ObjectType.LIBRARY;
			} else if (TypeGuards.isDocumentShare(srvData)) {
				// assume Acl is on a document
				this.webPath = `${settingsService.serverWebPath}/document/${srvData.DocumentId}`;
				this.id = srvData.DocumentACLId;
				this.type = ObjectType.DOCUMENT;
			}

			this.isOwnerAcl = this.id < 0;
			if (srvData.UserGroupId > 0) {
				this.userId = srvData.UserGroupId;
				this.userName = srvData.UserGroupName;
				this.isGroup = true;
			} else {
				this.userId = srvData.UserId;
				this.userName = srvData.UserName;
				this.isGroup = false;
			}

			this.elementId = this.generateUniqueId();

			if(isReceivedShareInfo(srvData) && isInheritedShare(srvData)) {
				this.sourceName = srvData.SourceFolderName || srvData.SourceLibraryName;
				this.sourceId = srvData.SourceFolderId || srvData.SourceLibraryId;
				this.sourceObjectType = +srvData.SourceLibraryId !== 0 ? ObjectType.LIBRARY : ObjectType.FOLDER;
				this.isInherited = true;
			}
		}

		if (isShareInfo(srvData)) {
			for (var i = 0; i < srvData.Embedded.Permissions.length; ++i) {
				var perm = srvData.Embedded.Permissions[i];
				if (perm.PermissionValue === PermissionType.FULLCONTROL) {
					if (perm.Allow) {
						this.allowPerms = -1;
						break;
					}
				} else {
					if (perm.Allow) {
						this.allowPerms += perm.PermissionValue;
					} else if (perm.Deny) {
						this.denyPerms += perm.PermissionValue;
					}
				}
			}
		}
		else if(angular.isDefined((srvData as any).Privileges) && (angular.isDefined((srvData as any).DenyPrivileges))){
			this.allowPerms = (srvData as any).Privileges;
			this.denyPerms = (srvData as any).DenyPrivileges;
		}

		this.updatePermissions();
	}

	public updatePermissions(perms?:PermissionType) {			
		var defaultType:ObjectType;
		var defaultPerms:number;

		if(typeof(perms) === 'number') {
			this.allowPerms = perms;
		}

		if (this.allowPerms === PermissionType.FULLCONTROL) {
			this.rightId = RightsType.FULLCONTROL;
			this.isModifiedRight = false;
			this.allowAdvancedSettings = false;
		}			
		else if (this.allowPerms & PermissionType.MODIFY) {
			// editors have the right to modify documents
			this.rightId = RightsType.EDIT;
			defaultType = this.type > ObjectType.FOLDER ? ObjectType.FOLDER : this.type;
			defaultPerms = this.Rights[this.rightId].defaultPerms[getObjectTypeName(defaultType)];
			this.isModifiedRight = (defaultPerms !== this.allowPerms);
			this.allowAdvancedSettings = true;
		}
		else if (this.type === ObjectType.FOLDER && ((this.allowPerms & 0x7FF) === PermissionType.UPLOADONLY)) {
			this.rightId = RightsType.UPLOADONLY;
			this.isModifiedRight = false;
			this.allowAdvancedSettings = true;
		}
		else if (this.allowPerms & PermissionType.READ) {
			// viewers have the right to read documents
			this.rightId = RightsType.VIEW;
			defaultType = this.type > ObjectType.FOLDER ? ObjectType.FOLDER : this.type;
			defaultPerms = this.Rights[this.rightId].defaultPerms[getObjectTypeName[defaultType]];
			this.isModifiedRight = (defaultPerms !== this.allowPerms);
			this.allowAdvancedSettings = true;
		}
		else {
			this.rightId = RightsType.NOACCESS;
			this.isModifiedRight = false;
			this.allowAdvancedSettings = false;
		}		

		return this;
	}	

	public hasRight(right:RightsType) {
		return this.rightId === right;
	}

	public get sourceString() {
		if (this.sourceObjectType === ObjectType.FOLDER) {
			return 'folder ' + this.sourceName;
		} else if (this.sourceObjectType === ObjectType.LIBRARY) {
			return 'library ' + this.sourceName;
		} else {
			return '';
		}
	}

	public get canModify() {
		return this.hasPermission(PermissionType.MODIFY);
	}

	public get canRead() {
		return this.hasPermission(PermissionType.READ);
	}

	public get canDownload() {
		return this.hasPermission(PermissionType.DOWNLOAD);
	}

	public get canUpload() {
		return this.hasPermission(PermissionType.ADDDOC);
	}

	public get canShareByLink() {
		return this.hasPermission(PermissionType.SHLINK + PermissionType.SHLINKR);
	}

	public hasAllPermissions(perms:PermissionType) {
		if (perms === PermissionType.FULLCONTROL) {
			return this.allowPerms === PermissionType.FULLCONTROL;
		}
		/* jshint -W016 */
		return ((this.allowPerms & perms) === perms);
		/* jshint +W016 */
	}

	public hasPermission(perms:PermissionType) {
		if (perms === PermissionType.FULLCONTROL) {
			return this.allowPerms === PermissionType.FULLCONTROL;
		}
		/* jshint -W016 */
		return ((this.allowPerms & perms) !== 0);
		/* jshint +W016 */
	}

	/*
	* @name Acl.toServer
	*
	* @description
	* This method creates a server representation of an acl so that it can be passed to the server's updatePermissions API
	*/
	public toServer() {
		var newAcl:ShareInfo = {
			UserId: this.isGroup ? 0 : this.userId,
			UserName: this.isGroup ? '' : this.userName,
			UserGroupId: this.isGroup ? this.userId : 0,
			UserGroupName: this.isGroup ? this.userName : '',
			RoleName: '' + this.rightId + (this.isModifiedRight ? '*' : ''),
			Embedded: {
				Permissions: []
			}
		}
		switch(this.type){
			case ObjectType.DOCUMENT:
				(newAcl as DocumentShare).DocumentACLId = +this.id;
				break;
			case ObjectType.FOLDER:
				(newAcl as FolderShare).FolderACLId = +this.id;
				break;
			case ObjectType.LIBRARY:
				(newAcl as LibraryShare).LibraryACLId = +this.id;
				break;
		}


		var permissionValue = PermissionType.FULLCONTROL;
		while (permissionValue <= PermissionsMetaData.Max) {
			var newPerm = {
				PermissionValue: permissionValue,
				Allow: false,
				Deny: false
			};

			if (this.rightId === RightsType.FULLCONTROL) {
				newPerm.Allow = true;
			}
			else if (permissionValue !== PermissionType.FULLCONTROL) {
				if (this.rightId === RightsType.NOACCESS) {
					newPerm.Deny = true;
				} else {
						// check to see if the bit at the specified position is set   
						// (permission granted) by ANDing and comparing to zero
					/* jshint -W016 */
					newPerm.Allow = (permissionValue & this.allowPerms) !== 0;
					/* jshint +W016 */
				}
			}
			newAcl.Embedded.Permissions.push(newPerm);

				/* jshint -W016 */
				permissionValue < 0 ? 1 : permissionValue << 1;
				permissionValue = permissionValue < 0 ? 1 : permissionValue << 1;
				/* jshint +W016 */
		}

		return newAcl;
	}

	/*
	* @name Acl.setRights
	*
	* @description
	* This method is called to change the rights settings of the acl.
	*/
	public setRights(right:RightsType, options: AdvancedOption[] = []) {
		right = +right;
		this.rightId = right;
		var defaultRight = this.Rights[right];
		var newPerms = defaultRight.defaultPerms[getObjectTypeName(this.type)];
			
		if (defaultRight.opts) {
			/* jshint -W016 */
			newPerms = newPerms & ~(PermissionsMetaData.All);
			/* jshint +W016 */
			for (var i = 0; i < options.length; i++) {
				var opt = options[i];
					var perms = opt.permissions;
				if (opt.apply === 'value') {
					if (opt.value === true) {
						newPerms = perms;
						break;
					}
				} else {
					/* jshint -W016 */
					if (opt.value === true) {
						newPerms |= perms;
					} else {
						newPerms &= ~perms;
					}
					/* jshint +W016 */
				}
			}
		}

		this.allowPerms = newPerms;
		this.updatePermissions();

		return this;
	}

	/*
	* @name Acl.getOptions
	*
	* @description
	* Returns a string with the right options that are set
	*/
	public getOptions() {
		var result = '';
		var right = this.Rights[this.rightId];
		var typeName = getObjectTypeName(this.type);

		if (right.defaultPerms[typeName] !== 0) {
			var options = this.RightsOptions[this.rightId];
			if (options !== null) {
					result = options.reduce((result, option) => {
					/* jshint -W016 */
					if ((this.allowPerms & option.mask[this.type]) !== 0) {
						var nm = option.name;
						result = result.length > 0 ? result + ', ' + nm : nm;
					}
					/* jshint +W016 */

						return result;
					}, result);
			}
		}

		return result;
	}
	
	public apply(otherAcl:Acl): Acl {
		this.allowPerms &= otherAcl.allowPerms;
		this.updatePermissions();
		return this;
	}

	private generateUniqueId() {
		return `acl-${this.util.generateUid()}`;
	}
}

function getObjectTypeName(objType:ObjectType): string {
	if(objType in ObjectType) {
		var typeName:string = ObjectType[objType];
		return typeName.toLowerCase();
	} else {
		throw new Error('Invalid object type.');
	}
}

/**
* TypeScript type guards
**/
function isServerAcl(srvData): srvData is ServerAcl{
	return angular.isDefined((srvData as ServerAcl).elementId);
}

function isLegacyLinkAcl(srvData): srvData is LegacyLinkAcl{
	return angular.isDefined((srvData as LegacyLinkAcl).AuthToken);
}						

function isLinkAcl(srvData): srvData is LinkAcl {
	return angular.isDefined((srvData as LinkAcl).ReferenceString);
	}

function isShareInfo(srvData): srvData is ShareInfo {
	return angular.isDefined((srvData as ShareInfo).Embedded) && 
		angular.isDefined((srvData as ShareInfo).Embedded.Permissions);
}

function isReceivedShareInfo(srvData): srvData is ReceivedShareInfo {
	return isShareInfo(srvData) && 
		angular.isDefined((srvData as ReceivedShareInfo).SourceFolderName) &&
		angular.isDefined((srvData as ReceivedShareInfo).SourceLibraryName);
}

function isInheritedShare(srvData): boolean {
	return isReceivedShareInfo(srvData) && 
		(srvData.SourceLibraryId > 0 || srvData.SourceFolderId > 0);
}





// WEBPACK FOOTER //
// ./src/phoenix/domain/acl.ts